/**
 * Shoppable video lightbox.
 *
 * Reads the tiles of the grid it belongs to, so it holds no content of its
 * own and the same code serves the homepage section and the PDP. Opening a
 * tile clones that tile's video template into the stage with sound available,
 * and the product panel is cloned from the tile as well.
 *
 * Nothing is downloaded until a tile is opened. Arrows and swipe move between
 * items, Escape closes, and <dialog> gives the focus trap and the backdrop.
 * The bag panel closes it when it opens (nyl-quick-add.js), and the close
 * event stops the video.
 */
(() => {
  class UgcViewer {
    constructor(dialog, grid) {
      this.dialog = dialog;
      this.grid = grid;
      this.tiles = Array.from(grid.querySelectorAll('[data-nyl-video]'));
      this.stage = dialog.querySelector('[data-nyl-ugc-video]');
      this.productSlot = dialog.querySelector('[data-nyl-ugc-product]');
      this.count = dialog.querySelector('[data-nyl-ugc-count]');
      this.playBtn = dialog.querySelector('[data-nyl-ugc-play]');
      this.muteBtn = dialog.querySelector('[data-nyl-ugc-mute]');
      this.playLabel = dialog.querySelector('[data-nyl-ugc-play-label]');
      this.index = -1;
      // Sound starts off each time the viewer opens. Once the shopper turns
      // it on, the next video keeps it on.
      this.soundOn = false;
      // Only a pause the shopper chose keeps the video still; a pause the
      // browser makes, as when the tab goes to the background, is undone.
      this.userPaused = false;

      // A single video has nowhere to go, so it gets no arrows and no count.
      this.single = this.tiles.length < 2;
      dialog.querySelectorAll('[data-nyl-ugc-prev], [data-nyl-ugc-next]').forEach((button) => {
        button.hidden = this.single;
      });
      if (this.count) this.count.hidden = this.single;

      this.tiles.forEach((tile, i) => {
        tile.addEventListener('click', (event) => {
          // The pause toggle and the product links keep their own jobs.
          if (event.target.closest('[data-nyl-video-toggle], a, button')) return;
          event.preventDefault();
          this.open(i);
        });
        tile.style.cursor = 'zoom-in';
      });

      dialog.querySelector('[data-nyl-ugc-close]')?.addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-nyl-ugc-prev]')?.addEventListener('click', () => this.step(-1));
      dialog.querySelector('[data-nyl-ugc-next]')?.addEventListener('click', () => this.step(1));
      this.playBtn?.addEventListener('click', () => this.togglePlay());
      this.muteBtn?.addEventListener('click', () => this.toggleMute());

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      // A close event that lands after the viewer has opened again leaves it be.
      dialog.addEventListener('close', () => { if (!dialog.open) this.teardown(); });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
      });

      this.addSwipe();

      // Browsers pause silent video in a background tab to save power. Back
      // on the page, the viewer plays again unless the shopper paused it.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden || !this.dialog.open || this.userPaused) return;
        const video = this.video;
        if (video?.paused) video.play().catch(() => {});
      });
    }

    addSwipe() {
      let startX = null;
      this.stage.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      this.stage.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 60) this.step(dx < 0 ? 1 : -1);
      });
    }

    open(index) {
      this.show(index);
      if (!this.dialog.open) {
        this.holdTiles();
        this.dialog.showModal();
        // The viewer holds focus rather than its first button, so a tap does
        // not light up a focus ring; Tab still reaches every control.
        this.dialog.focus({ preventScroll: true });
      }
    }

    // The tiles behind the viewer stop while it is open, so a phone plays one
    // video at a time, and carry on when it closes unless the shopper had
    // paused them. They are hidden as well: some browsers draw a paused video
    // on its own layer, over the viewer, in the tile's place.
    holdTiles() {
      this.grid.classList.add('is-viewing');
      this.tiles.forEach((tile) => {
        const video = tile.querySelector('video');
        if (video && !video.paused) {
          video.pause();
          tile.dataset.nylHeld = 'true';
        }
      });
    }

    releaseTiles() {
      this.grid.classList.remove('is-viewing');
      this.tiles.forEach((tile) => {
        if (tile.dataset.nylHeld !== 'true') return;
        delete tile.dataset.nylHeld;
        if (tile.dataset.userPaused === 'true') return;
        tile.querySelector('video')?.play().catch(() => {});
      });
    }

    step(delta) {
      if (this.single) return;
      this.show((this.index + delta + this.tiles.length) % this.tiles.length);
    }

    show(index) {
      this.index = index;
      this.userPaused = false;
      const tile = this.tiles[index];
      const source = tile.querySelector('[data-nyl-video-source]');

      this.stage.replaceChildren();
      if (source) {
        // The same 720p file the tile plays, so it is usually cached already.
        this.stage.append(source.content.cloneNode(true));
        const video = this.stage.querySelector('video');
        if (video) {
          // Muted unless the shopper turned sound on, which a browser allows
          // after their tap. If it still refuses, the video plays muted.
          video.muted = !this.soundOn;
          video.volume = 1;
          video.loop = true;
          video.controls = false;
          video.playsInline = true;
          video.addEventListener('play', () => this.setPlayState(true));
          video.addEventListener('pause', () => this.setPlayState(false));
          video.play().catch(() => {
            if (video.muted) {
              this.setPlayState(false);
              return;
            }
            video.muted = true;
            this.soundOn = false;
            this.setMuteState(true);
            video.play().catch(() => this.setPlayState(false));
          });
        }
      }

      const product = tile.querySelector('.nyl-video__product');
      this.productSlot.replaceChildren();
      if (product) this.productSlot.append(product.cloneNode(true));

      if (this.count) this.count.textContent = `${index + 1} / ${this.tiles.length}`;
      this.setMuteState(!this.soundOn);
    }

    get video() {
      return this.stage.querySelector('video');
    }

    setPlayState(playing) {
      if (!this.playBtn) return;
      this.playBtn.dataset.state = playing ? 'playing' : 'paused';
      if (this.playLabel) {
        this.playLabel.textContent = playing ? this.playBtn.dataset.labelPause : this.playBtn.dataset.labelPlay;
      }
    }

    togglePlay() {
      const video = this.video;
      if (!video) return;
      this.userPaused = !video.paused;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }

    // The button reads Turn sound on, and is pressed while sound is on.
    setMuteState(muted) {
      if (!this.muteBtn) return;
      this.muteBtn.dataset.state = muted ? 'muted' : 'loud';
      this.muteBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    }

    toggleMute() {
      const video = this.video;
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted && video.volume === 0) video.volume = 1;
      this.soundOn = !video.muted;
      this.setMuteState(video.muted);
    }

    teardown() {
      // Stop the download the moment it closes.
      const video = this.video;
      if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
      this.stage.replaceChildren();
      this.productSlot.replaceChildren();
      this.index = -1;
      this.soundOn = false;
      this.userPaused = false;
      this.releaseTiles();
    }
  }

  const start = () => {
    document.querySelectorAll('[data-nyl-ugc]').forEach((dialog) => {
      const grid = dialog.closest('.nyl-section')?.querySelector('.nyl-videos')
        || document.querySelector(`[data-nyl-ugc-grid="${dialog.id}"]`);
      if (grid) new UgcViewer(dialog, grid);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
